import { useCallback } from "react";
import { useAction } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import type { Reference, ReferenceType } from "../../../shared/types/reference";
import type { SuggestionManagerState, SuggestionManagerActions } from "./useSuggestionManager";
import { toast } from "sonner";

interface UseFormSubmissionProps {
  existingReference?: Reference | null;
  suggestionManager: SuggestionManagerState & SuggestionManagerActions;
  onFormClose: () => void;
  // Form mutations
  createReference: any;
  updateReference: any;
  generateUploadUrl: any;
  addTagToRef: any;
  createApp: any;
}

interface FormData {
  title: string;
  type: ReferenceType;
  description: string;
  sourceURL: string;
  category: string;
  imageFiles: File[];
  currentTags: Array<{ _id: Id<"tags">, name: string }>;
  selectedAppId: Id<"apps"> | "new" | null;
  newApp: { name: string; url: string; description: string; category: string };
  creatingApp: boolean;
  resetForm: () => void;
  extractedThumbnail?: string | null;
  isPublic: boolean;
}

// Helper function to convert string confidence to number for database storage
function confidenceToNumber(confidence: 'low' | 'medium' | 'high' | undefined): number | undefined {
  if (!confidence) return undefined;
  switch (confidence) {
    case 'low': return 1;
    case 'medium': return 2;
    case 'high': return 3;
    default: return undefined;
  }
}

export function useFormSubmission({
  existingReference,
  suggestionManager,
  onFormClose,
  createReference,
  updateReference,
  generateUploadUrl,
  addTagToRef,
  createApp
}: UseFormSubmissionProps) {
  const analyzeMediaForSuggestions = useAction(api.formAutoFill.analyzeMediaForFormSuggestions);

  const handleAutoFillWithAI = useCallback(async (imageFiles: File[]) => {
    console.log('Auto-fill AI called at:', new Date().toISOString(), 'with files:', imageFiles.length);
    
    if (imageFiles.length === 0) {
      toast.error("Please upload at least one image or media file to use AI auto-fill");
      return;
    }

    try {
      suggestionManager.setAnalysisState('analyzing');
      
      // Upload images to Convex storage temporarily and get URLs
      const storageIds: Id<"_storage">[] = [];
      const mediaTypes: ('image' | 'video')[] = [];
      
      console.log('Processing files:', imageFiles.map(f => ({ name: f.name, type: f.type, size: f.size })));
      
      for (const file of imageFiles) {
        // Only process image files for AI analysis (OpenAI Vision doesn't support video directly)
        if (file.type.startsWith('image/')) {
          console.log('Uploading image file for AI analysis:', file.name);
          
          // Check file size (OpenAI has limits on image size)
          const maxSizeInMB = 20; // OpenAI limit is 20MB
          const fileSizeInMB = file.size / (1024 * 1024);
          
          if (fileSizeInMB > maxSizeInMB) {
            console.warn(`Image file ${file.name} is too large: ${fileSizeInMB.toFixed(2)}MB (max: ${maxSizeInMB}MB)`);
            toast.warning(`Image "${file.name}" is too large (${fileSizeInMB.toFixed(1)}MB). Maximum size is ${maxSizeInMB}MB.`);
            continue; // Skip this file
          }
          
          try {
            // Upload to Convex storage
            const uploadUrl = await generateUploadUrl();
            const uploadResult = await fetch(uploadUrl, {
              method: "POST",
              headers: { "Content-Type": file.type },
              body: file,
            });
            
            if (!uploadResult.ok) {
              throw new Error(`Failed to upload ${file.name}: ${uploadResult.statusText}`);
            }
            
            const { storageId } = await uploadResult.json();
            storageIds.push(storageId);
            mediaTypes.push('image');
            
            console.log('Successfully uploaded image for AI analysis:', file.name, 'storageId:', storageId);
          } catch (uploadError) {
            console.error('Failed to upload image:', file.name, uploadError);
            toast.error(`Failed to upload ${file.name}. Please try again.`);
            continue;
          }
        } else if (file.type.startsWith('video/')) {
          // For videos, we could extract a frame, but for now skip them
          console.warn('Video files are not supported for AI analysis yet:', file.name);
        }
      }

      if (storageIds.length === 0) {
        toast.error("No image files could be uploaded for AI analysis. Please try again.");
        suggestionManager.setAnalysisState('idle');
        return;
      }

      // Prepare existing form data context
      const existingData = {
        title: undefined,
        description: undefined,
        sourceURL: undefined,
        category: undefined,
        tags: []
      };

      console.log('Calling AI analysis with storage IDs:', storageIds);

      // Call the AI analysis action with storage IDs
      const response = await analyzeMediaForSuggestions({
        storageIds,
        mediaTypes,
        existingData
      });

      console.log('AI analysis response received:', response);

      // Set the suggestions in the manager
      suggestionManager.setSuggestions(response);
      
      toast.success(`AI analysis complete! Found ${response.suggestions.length} suggestions.`);
      
    } catch (error) {
      console.error('AI analysis error:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : undefined
      });
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to analyze media with AI';
      suggestionManager.setAnalysisState('error');
      suggestionManager.setAnalysisError(errorMessage);
      
      // Provide more specific error messages based on error type
      if (errorMessage.includes('Insufficient tokens')) {
        toast.error('Not enough tokens for AI analysis. You need 3 tokens for this operation.');
      } else if (errorMessage.includes('Rate limit')) {
        toast.error('Too many requests. Please wait a moment and try again.');
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error(`AI analysis failed: ${errorMessage}`);
      }
    }
  }, [analyzeMediaForSuggestions, suggestionManager, generateUploadUrl]);

  const applyAcceptedSuggestions = useCallback((formData: FormData) => {
    const acceptedValues = suggestionManager.getAcceptedValues();
    const result = { ...formData };
    
    Object.entries(acceptedValues).forEach(([field, value]) => {
      switch (field) {
        case 'title':
          if (!result.title) result.title = value;
          break;
        case 'description':
          if (!result.description) result.description = value;
          break;
        case 'sourceURL':
          if (!result.sourceURL) result.sourceURL = value;
          break;
        case 'category':
          if (!result.category) result.category = value;
          break;
        case 'type':
          if (value && ['screenshot', 'component', 'flow', 'screencast'].includes(value)) {
            result.type = value as ReferenceType;
          }
          break;
        case 'tags': {
          // Parse comma-separated tags and add them
          const newTags = value.split(',').map(tag => tag.trim()).filter(Boolean);
          newTags.forEach(tagName => {
            if (!result.currentTags.find(t => t.name === tagName)) {
              result.currentTags.push({ 
                _id: undefined as unknown as Id<"tags">, 
                name: tagName 
              });
            }
          });
          break;
        }
        case 'appName': {
          if (!result.selectedAppId) {
            // Pre-fill new app form
            result.newApp = { ...result.newApp, name: value };
          }
          break;
        }
      }
    });

    return result;
  }, [suggestionManager]);

  const handleSubmit = useCallback(async (formData: FormData) => {
    console.log('[Form Submission] Starting handleSubmit with formData:', {
      title: formData.title,
      type: formData.type,
      hasDescription: !!formData.description,
      hasSourceURL: !!formData.sourceURL,
      hasCategory: !!formData.category,
      imageFilesCount: formData.imageFiles.length,
      currentTagsCount: formData.currentTags.length,
      hasSelectedAppId: !!formData.selectedAppId,
      hasExtractedThumbnail: !!formData.extractedThumbnail,
      extractedThumbnail: formData.extractedThumbnail
    });

    // Apply any remaining accepted suggestions before submission
    const updatedFormData = applyAcceptedSuggestions(formData);
    
    console.log('[Form Submission] After applying accepted suggestions:', {
      titleChanged: updatedFormData.title !== formData.title,
      newTitle: updatedFormData.title,
      hasExtractedThumbnail: !!updatedFormData.extractedThumbnail,
      extractedThumbnail: updatedFormData.extractedThumbnail,
      imageFilesCount: updatedFormData.imageFiles.length
    });
    
    // Validate required fields (considering AI suggestions)
    const effectiveTitle = updatedFormData.title || suggestionManager.getAcceptedValue('title') || '';
    
    if (!effectiveTitle.trim()) {
      console.error('[Form Submission] Validation failed: Title is required');
      toast.error("Title is required");
      return;
    }
    
    // Check if media is required and available (either uploaded files, existing image, or extracted thumbnail)
    const requiresMedia = (updatedFormData.type === "screenshot" || updatedFormData.type === "component" || updatedFormData.type === "flow");
    const hasUploadedFiles = updatedFormData.imageFiles.length > 0;
    const hasExistingImage = !!existingReference?.imageUrl;
    const hasExtractedThumbnail = !!updatedFormData.extractedThumbnail;
    
    console.log('[Form Submission] Media validation check:', {
      requiresMedia,
      hasUploadedFiles,
      hasExistingImage,
      hasExtractedThumbnail,
      existingImageUrl: existingReference?.imageUrl || 'none'
    });
    
    if (requiresMedia && !hasUploadedFiles && !hasExistingImage && !hasExtractedThumbnail) {
      console.error('[Form Submission] Validation failed: Media file is required');
      toast.error("Media file is required for this reference type");
      return;
    }

    console.log('[Form Submission] Validation passed, proceeding with submission...');

    try {
      let finalAppId = updatedFormData.selectedAppId;
      
      // Handle new app creation (including AI-suggested app names)
      if (updatedFormData.creatingApp || (suggestionManager.getAcceptedValue('appName') && !updatedFormData.selectedAppId)) {
        console.log('[Form Submission] Creating new app...');
        const appName = updatedFormData.newApp.name || suggestionManager.getAcceptedValue('appName') || '';
        if (!appName.trim()) {
          console.error('[Form Submission] App creation failed: App name is required');
          toast.error("App name is required when creating a new app");
          return;
        }
        
        const appData = {
          name: appName,
          url: updatedFormData.newApp.url || suggestionManager.getAcceptedValue('sourceURL') || '',
          category: updatedFormData.newApp.category || suggestionManager.getAcceptedValue('category') || '',
          description: updatedFormData.newApp.description || suggestionManager.getAcceptedValue('description') || ''
        };
        
        finalAppId = await createApp(appData);
        console.log('[Form Submission] New app created with ID:', finalAppId);
      }

      // Prepare reference data with AI suggestions as fallbacks
      console.log('[Form Submission] Preparing reference data...');
      const referenceData: any = {
        title: effectiveTitle,
        type: updatedFormData.type,
        description: updatedFormData.description || suggestionManager.getAcceptedValue('description') || '',
        sourceURL: updatedFormData.sourceURL || suggestionManager.getAcceptedValue('sourceURL') || '',
        category: updatedFormData.category || suggestionManager.getAcceptedValue('category') || '',
        appId: finalAppId && finalAppId !== "new" ? finalAppId : undefined,
        aiSuggestions: {
          used: suggestionManager.acceptedCount > 0,
          acceptedFields: Object.keys(suggestionManager.getAcceptedValues()),
          overallConfidence: confidenceToNumber(suggestionManager.overallConfidence),
          analysisNotes: suggestionManager.analysisNotes
        },
        isPublic: formData.isPublic
      };

      console.log('[Form Submission] Initial reference data prepared:', {
        title: referenceData.title,
        type: referenceData.type,
        hasDescription: !!referenceData.description,
        hasSourceURL: !!referenceData.sourceURL,
        hasCategory: !!referenceData.category,
        hasAppId: !!referenceData.appId,
        hasAiSuggestions: !!referenceData.aiSuggestions
      });

      // Only set existing image/video URLs if no new files are being uploaded
      if (updatedFormData.imageFiles.length === 0 && existingReference) {
        console.log('[Form Submission] No new files and existing reference found, checking existing media...');
        if (existingReference.imageUrl) {
          referenceData.imageUrl = existingReference.imageUrl;
          console.log('[Form Submission] Set existing imageUrl:', existingReference.imageUrl);
        }
        if (existingReference.videoUrl) {
          referenceData.videoUrl = existingReference.videoUrl;
          console.log('[Form Submission] Set existing videoUrl:', existingReference.videoUrl);
        }
      }

      // Set extracted thumbnail as image URL if no files uploaded and no existing image
      if (updatedFormData.imageFiles.length === 0 && !existingReference?.imageUrl && updatedFormData.extractedThumbnail) {
        console.log('[Form Submission] Setting extracted thumbnail as imageUrl:', updatedFormData.extractedThumbnail);
        referenceData.imageUrl = updatedFormData.extractedThumbnail;
        console.log('[Form Submission] ✅ Extracted thumbnail successfully added to referenceData');
      } else {
        console.log('[Form Submission] Not setting extracted thumbnail because:', {
          hasUploadedFiles: updatedFormData.imageFiles.length > 0,
          hasExistingImage: !!existingReference?.imageUrl,
          hasExtractedThumbnail: !!updatedFormData.extractedThumbnail,
          reason: updatedFormData.imageFiles.length > 0 ? 'has uploaded files' : 
                  existingReference?.imageUrl ? 'has existing image' : 
                  !updatedFormData.extractedThumbnail ? 'no extracted thumbnail' : 'unknown'
        });
      }

      // Handle file uploads
      if (updatedFormData.imageFiles.length > 0) {
        console.log('[Form Submission] Processing file uploads for', updatedFormData.imageFiles.length, 'files...');
        const uploadPromises = updatedFormData.imageFiles.map(async (file: File) => {
          console.log('[Form Submission] Uploading file:', { name: file.name, size: file.size, type: file.type });
          const uploadUrl = await generateUploadUrl();
          const result = await fetch(uploadUrl, {
            method: "POST",
            headers: { "Content-Type": file.type },
            body: file,
          });
          return await result.json();
        });

        const uploadResults = await Promise.all(uploadPromises);
        console.log('[Form Submission] All files uploaded successfully, results:', uploadResults);
        
        // Use the new storageIds approach for proper file handling
        if (uploadResults.length > 0) {
          const storageIds = uploadResults.map(result => result.storageId);
          console.log('[Form Submission] Setting storageIds:', storageIds);
          // Add storageIds to reference data instead of setting imageUrl/videoUrl
          referenceData.storageIds = storageIds;
          // For backward compatibility, also set the first storage ID
          referenceData.storageId = storageIds[0];
          console.log('[Form Submission] Set main storageId for compatibility:', storageIds[0]);
        }
      } else {
        console.log('[Form Submission] No files to upload');
      }

      console.log('[Form Submission] Final reference data before submission:', {
        title: referenceData.title,
        type: referenceData.type,
        hasDescription: !!referenceData.description,
        hasSourceURL: !!referenceData.sourceURL,
        hasCategory: !!referenceData.category,
        hasStorageId: !!referenceData.storageId,
        storageId: referenceData.storageId || 'none',
        hasStorageIds: !!referenceData.storageIds,
        storageIdsLength: referenceData.storageIds?.length || 0,
        hasImageUrl: !!referenceData.imageUrl,
        imageUrl: referenceData.imageUrl || 'none',
        hasVideoUrl: !!referenceData.videoUrl,
        hasAppId: !!referenceData.appId,
        hasAiSuggestions: !!referenceData.aiSuggestions
      });

      let referenceId: Id<"references">;
      
      if (existingReference) {
        console.log('[Form Submission] Updating existing reference:', existingReference._id);
        await updateReference({
          id: existingReference._id,
          ...referenceData,
        });
        referenceId = existingReference._id;
        toast.success("Reference updated successfully!");
        console.log('[Form Submission] Reference updated successfully');
      } else {
        console.log('[Form Submission] Creating new reference...');
        referenceId = await createReference(referenceData);
        toast.success("Reference created successfully!");
        console.log('[Form Submission] Reference created successfully with ID:', referenceId);
      }

      // Handle tags (including AI-suggested tags)
      const allTags = [...updatedFormData.currentTags];
      const aiSuggestedTags = suggestionManager.getAcceptedValue('tags');
      if (aiSuggestedTags) {
        const newTags = aiSuggestedTags.split(',').map(tag => tag.trim()).filter(Boolean);
        newTags.forEach(tagName => {
          if (!allTags.find(t => t.name === tagName)) {
            allTags.push({ _id: undefined as unknown as Id<"tags">, name: tagName });
          }
        });
      }

      console.log('[Form Submission] Adding', allTags.length, 'tags to reference...');
      // Add tags to reference
      for (const tag of allTags) {
        await addTagToRef({ referenceId, tagName: tag.name });
      }
      console.log('[Form Submission] All tags added successfully');

      // Track AI suggestion usage analytics
      if (suggestionManager.acceptedCount > 0) {
        console.log('AI Suggestions Analytics:', {
          totalSuggestions: suggestionManager.totalCount,
          acceptedSuggestions: suggestionManager.acceptedCount,
          rejectedSuggestions: suggestionManager.rejectedCount,
          acceptedFields: Object.keys(suggestionManager.getAcceptedValues()),
          overallConfidence: suggestionManager.overallConfidence
        });
      }

      console.log('[Form Submission] Submission completed successfully, resetting form...');
      // Reset form and close
      formData.resetForm();
      suggestionManager.clearAllSuggestions();
      onFormClose();
      console.log('[Form Submission] Form reset and closed');
      
    } catch (error) {
      console.error("[Form Submission] Error saving reference:", error);
      toast.error("Failed to save reference. Please try again.");
    }
  }, [
    existingReference,
    suggestionManager,
    onFormClose,
    createReference,
    updateReference,
    generateUploadUrl,
    addTagToRef,
    createApp,
    applyAcceptedSuggestions
  ]);

  return {
    handleAutoFillWithAI,
    handleSubmit
  };
} 